import { ChevronRight, Copy, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { obfuscateUUID } from '@/lib/incognito'

export const UUID_REGEX = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi
// Systems are processed in batches of this size. Actual network concurrency is
// further capped by the HTTP client's global request throttle (see httpClient.ts).
export const PARALLEL_CHECK_SIZE = 5

export function detectDelimiter(line: string): string {
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0 }
  for (const ch of line) {
    if (ch in counts) counts[ch]++
  }
  const best = Object.entries(counts).reduce((a, b) => (b[1] > a[1] ? b : a))
  return best[1] > 0 ? best[0] : ','
}

export function parseCSV(text: string): { headers: string[]; rows: string[][] } | null {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '')
  if (lines.length === 0) return null
  const delimiter = detectDelimiter(lines[0])
  const allRows = lines.map((l) => l.split(delimiter))
  const colCount = allRows[0].length
  if (colCount < 2) return null

  const firstRowHasUUID = allRows[0].some((cell) => UUID_REGEX.test(cell))
  UUID_REGEX.lastIndex = 0

  let headers: string[]
  let rows: string[][]
  if (firstRowHasUUID) {
    headers = allRows[0].map((_, i) => `Column ${i + 1}`)
    rows = allRows
  } else {
    headers = allRows[0].map((h) => h.trim())
    rows = allRows.slice(1)
  }

  return { headers, rows }
}

export function parseIDs(text: string): string[] {
  const matches = text.match(UUID_REGEX) ?? []
  return [...new Set(matches.map((id) => id.toLowerCase()))]
}

export interface FailureGroup {
  reason: string
  systemIDs: string[]
}

function normalizeReason(reason: string): string {
  return reason.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '…')
}

export function groupFailuresByReason(
  failures: Array<{ systemID: string; reason: string }>
): FailureGroup[] {
  const byReason = new Map<string, string[]>()
  for (const { systemID, reason } of failures) {
    const key = normalizeReason(reason || 'Unknown error')
    const ids = byReason.get(key)
    if (ids) ids.push(systemID)
    else byReason.set(key, [systemID])
  }
  return [...byReason.entries()]
    .map(([reason, systemIDs]) => ({ reason, systemIDs }))
    .sort((a, b) => b.systemIDs.length - a.systemIDs.length)
}

export function SummaryCard({
  label,
  value,
  colorClass,
  copyIds,
}: {
  label: string
  value: number
  colorClass: string
  copyIds?: string[]
}) {
  return (
    <div className="rounded-md border p-3 text-center relative">
      <p className={cn('text-2xl font-bold', colorClass)}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      {copyIds && copyIds.length > 0 && (
        <button
          className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-foreground"
          title="Copy IDs"
          onClick={() => navigator.clipboard.writeText(copyIds.join('\n'))}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

export function FailureBreakdown({
  failures,
  isIncognito,
}: {
  failures: Array<{ systemID: string; reason: string }>
  isIncognito: boolean
}) {
  if (failures.length === 0) return null
  const groups = groupFailuresByReason(failures)
  return (
    <div className="rounded-md border border-destructive/40 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-destructive/10 border-b border-destructive/40">
        <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
        <span className="text-sm font-medium text-destructive">
          {failures.length} system{failures.length !== 1 ? 's' : ''} failed — broken down by reason
        </span>
      </div>
      <div className="divide-y">
        {groups.map((group, idx) => (
          <details key={idx} className="group">
            <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/30 list-none">
              <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
              <span className="text-sm flex-1">{group.reason}</span>
              <span className="text-xs font-medium text-destructive flex-shrink-0">
                {group.systemIDs.length}
              </span>
              {!isIncognito && (
                <button
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                  title="Copy IDs"
                  onClick={(e) => {
                    e.preventDefault()
                    navigator.clipboard.writeText(group.systemIDs.join('\n'))
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
            </summary>
            <div className="px-3 pb-2 pl-9 space-y-0.5">
              {group.systemIDs.map((id) => (
                <p key={id} className="text-xs font-mono text-muted-foreground">
                  {isIncognito ? obfuscateUUID(id) : id}
                </p>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  )
}
